import { useCallback, useEffect, useState } from 'react';

interface IPaginate<T> {
  results: T[];
  count: number;
  pages: number;
  page: number;
}

export default function usePaginate<T>({
  call,
  values,
  skip,
  limit = 10,
}: {
  /** Апи функция */
  call: (param?: any) => Promise<IPaginate<T>>;
  /** Параметры для апи функции */
  values?: any;
  /** Ввызывать асинхронный вызов при инифиализации  */
  skip?: boolean;
  /** Кол-во элементов на одной странице */
  limit?: number;
}) {
  const [data, setData] = useState<T[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [error, setError] = useState<unknown>();

  const params = JSON.stringify(values);

  const refetchData = useCallback(
    async params => {
      setIsLoading(true);
      try {
        const { results, count, pages, page } = await call({
          ...params,
          limit,
          page: 1,
        });

        setData(results);
        setHasNext(page < pages);
      } catch (err) {
        setError(err);
      }
      setIsLoading(false);
    },
    [call, limit]
  );

  const loadMore = useCallback(
    async requestParams => {
      if (!hasNext || !Array.isArray(data)) return;

      const params = {
        ...requestParams,
        page: data.length / limit + 1,
        limit,
      };
      setIsLoading(true);
      try {
        const { results, pages, page } = await call(params);
        if (results && results.length) {
          setData(data.concat(results));
        }
        setHasNext(page < pages);
      } catch (err) {
        setError(err);
      }
      setIsLoading(false);
    },
    [hasNext, data, limit, call]
  );

  useEffect(() => {
    if (!skip) {
      refetchData(params && JSON.parse(params)).then();
    }
  }, [skip, refetchData, params]);

  return { data, isLoading, hasNext, error, loadMore, refetchData };
}
